import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import CloseIcon from '@mui/icons-material/Close';
import DeleteRounded from '@mui/icons-material/DeleteRounded';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
import { alpha, useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';

// import your real type:
import { Button, Chip, LinearProgress, Paper, Stack, useMediaQuery } from '@mui/material';
import { OnlyFansSubscriber } from '../../hooks/useSubscriber';
import useSubscriberVaultMediaReleaseForms from '../../hooks/useSubscriberVaultMediaReleaseForms';
import { VaultMediaResponseList } from '../../hooks/useSubscriberVaultMedias';
import { stripHtmlAndConvertParagraphs } from '../../utils/common';
import DialogAdaptive from '../common/DialogAdaptive';
import OnlyFansTagCreatorsSelector from '../common/OnlyFansTagCreatorsSelector';
import StyledCardTitleBar from '../common/StyledCardTitleBar';
import StyledDialogActions from '../common/StyledDialogActions';
import VaultMenu from './VaultMenu';
import VaultTile from './VaultTile';

// Memoized SelectedChips component
const SelectedChips = React.memo(({ items, onDelete }: { items: { id: number; name: string }[]; onDelete: (id: number) => void }) => {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <Stack direction="row" flexWrap="wrap" alignItems="center" spacing={2} rowGap={1} justifyContent="flex-start" marginX={0}>
                {items.map(item => (
                    <Chip key={item.id} label={stripHtmlAndConvertParagraphs(item.name)} onDelete={() => onDelete(item.id)} size="small" />
                ))}
            </Stack>
        </Box>
    );
});

type CompactPriceFieldProps = {
    value: string;
    onChange: (v: string) => void;
    width?: number;
    min?: number; // default 3
    max?: number; // default 200
};

function CompactPriceField({ value, onChange, width = 75, min = 0, max = 200 }: CompactPriceFieldProps) {
    // live filter only — digits + optional dot + max 2 decimals; never reformat while typing
    const handleChange = (next: string) => {
        if (next === '' || /^\d*\.?\d{0,2}$/.test(next)) {
            onChange(next);
        }
    };

    // validation / formatting happens once the user leaves the field
    const handleBlur = () => {
        if (value === '') return;

        const n = Number(value);

        if (!Number.isFinite(n)) return onChange('');

        // exactly 0 is allowed; anything else below $3 is invalid
        if (n === 0) return onChange('0.00');
        if (n < 3) return onChange('');

        const clamped = Math.min(Math.max(n, min), max);

        onChange(clamped.toFixed(2));
    };

    return (
        <OutlinedInput
            value={value}
            onChange={e => handleChange(e.target.value)}
            onFocus={e => e.target.select()}
            onBlur={handleBlur}
            placeholder="0.00"
            size="small"
            startAdornment={
                <InputAdornment position="start" sx={{ ml: -1.2, display: 'flex', alignItems: 'left' }}>
                    $
                </InputAdornment>
            }
            inputProps={{ inputMode: 'decimal', 'aria-label': 'locked price' }}
            sx={{
                width,
                height: 20, // compact control height
                marginTop: '0px',
                alignSelf: 'center', // ✅ vertical align in the row
                display: 'flex',
                alignItems: 'center', // center input/adornment vertically
                borderRadius: 1,
                // '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                // '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'text.secondary' },
                // '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },

                // // tighten inner paddings to match caption
                '& .MuiInputBase-input': {
                    height: 20,
                    boxSizing: 'border-box',
                    padding: 0,
                    paddingLeft: 0, // we have $ adornment
                    paddingRight: 1,
                    fontSize: '0.75rem', // caption size
                    lineHeight: 1, // prevents vertical misalignment
                    textAlign: 'right',
                },
                // '& .MuiInputAdornment-root': {
                //     height: '100%',
                //     // alignItems: 'center',
                // },
                // // remove extra notch space
                // '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
            }}
        />
    );
}

type Props = {
    subscriber: OnlyFansSubscriber;
    // selectedItems: VaultMediaResponseList[];
    unlockedItems: VaultMediaResponseList[];
    setUnlockedItems: React.Dispatch<React.SetStateAction<VaultMediaResponseList[]>>;
    lockedItems: VaultMediaResponseList[];
    setlockedItems: React.Dispatch<React.SetStateAction<VaultMediaResponseList[]>>;
    isLoading: boolean;
    taggedCreators: { id: number; name: string }[];
    onChangeTaggedCreators: (creators: { id: number; name: string }[]) => void;
    lockedPrice: string;
    onPriceChanged: (newPrice: string) => void;
    taggedReleaseForms: { id: number; name: string }[];
    setTaggedReleaseForms: React.Dispatch<React.SetStateAction<{ id: number; name: string }[]>>;
};

const HScroll: React.FC<React.ComponentProps<typeof Box>> = ({ sx, ...props }) => {
    // const theme = useTheme();
    // const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'nowrap', // one row
                gap: 1,
                overflowX: 'auto',
                overflowY: 'hidden',
                alignItems: 'center',
                px: 1,
                py: 1,
                width: '100%',
                height: '100%',
                WebkitOverflowScrolling: 'touch',
                touchAction: 'pan-x', // allow horizontal swipes
                '& > *': {
                    flex: '0 0 auto', // prevent shrinking; create overflow when needed
                },
                // // Optional: hide scrollbars on mobile
                // ...(isMobile && {
                //     scrollbarWidth: 'none',
                //     msOverflowStyle: 'none',
                //     '&::-webkit-scrollbar': { display: 'none' },
                // }),
                ...sx,
            }}
            {...props}
        />
    );
};

export default function MediaHolder({
    subscriber,
    unlockedItems,
    setUnlockedItems,
    lockedItems,
    setlockedItems,
    isLoading,
    taggedCreators,
    onChangeTaggedCreators,
    taggedReleaseForms,
    setTaggedReleaseForms,
    lockedPrice,
    onPriceChanged,
}: Props) {
    const theme = useTheme();
    const rightBg = alpha(theme.palette.primary.main, 0.08);
    const rightHeaderBg = alpha(theme.palette.primary.main, 0.12);
    const rightHeaderBorder = alpha(theme.palette.primary.main, 0.25);

    const { getVaultMediaTaggedItems } = useSubscriberVaultMediaReleaseForms();

    // const [insidelockedPrice, setLockedPrice] = React.useState<string>('');

    // media
    const [selectedItems, setSelectedItems] = useState<VaultMediaResponseList[]>([]);

    const [openTagCreators, setOpenTagCreators] = useState(false);

    // const [insidetaggedCreators, setTaggedCreators] = useState<{ id: number; name: string }[]>([]);
    // const [taggedReleaseForms, setTaggedReleaseForms] = useState<{ id: number; name: string }[]>([]);
    const [tagCreators, setTagCreators] = useState<boolean>(true);

    const [unlockedItemsSelected, setUnlockedItemsSelected] = useState<VaultMediaResponseList[]>([]);
    const [lockedItemsSelected, setlockedItemsSelected] = useState<VaultMediaResponseList[]>([]);

    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [open, setOpen] = useState(false);

    const onPriceChange = (item: string) => {
        onPriceChanged(item);
    };

    const onMoveSelectedToUnlocked = () => {
        if (!lockedItemsSelected.length) return;

        const selectedIds = new Set(lockedItemsSelected.map(i => i.id));

        // 1) Add selected (from locked) to unlocked, without duplicates.
        setUnlockedItems(prev => {
            const seen = new Set(prev.map(i => i.id));
            const toAdd = lockedItemsSelected.filter(i => !seen.has(i.id));
            return prev.concat(toAdd); // keeps existing order, appends in the order they were selected
        });

        // 2) Remove selected from locked.
        setlockedItems(prev => prev.filter(i => !selectedIds.has(i.id)));

        // 3) Clear locked selection (optional).
        setlockedItemsSelected?.([]);

        // 4) If you track unlocked selection, optionally remove dupes or add them selected.
        // Here we remove any now-moved ids from unlocked selection (adjust if you prefer to add instead).
        setUnlockedItemsSelected?.(prev => prev.filter(i => !selectedIds.has(i.id)));
    };

    const onMoveSelectedToLocked = () => {
        if (!unlockedItemsSelected.length) return;

        const selectedIds = new Set(unlockedItemsSelected.map(i => i.id));

        // 1) Add selected (from locked) to unlocked, without duplicates.
        setlockedItems(prev => {
            const seen = new Set(prev.map(i => i.id));
            const toAdd = unlockedItemsSelected.filter(i => !seen.has(i.id));
            return prev.concat(toAdd); // keeps existing order, appends in the order they were selected
        });

        // 2) Remove selected from locked.
        setUnlockedItems(prev => prev.filter(i => !selectedIds.has(i.id)));

        // 3) Clear locked selection (optional).
        setUnlockedItemsSelected?.([]);

        // 4) If you track unlocked selection, optionally remove dupes or add them selected.
        // Here we remove any now-moved ids from unlocked selection (adjust if you prefer to add instead).
        setlockedItemsSelected?.(prev => prev.filter(i => !selectedIds.has(i.id)));
    };

    const onDeleteSelected = () => {
        const idsToRemove = new Set<number>([...unlockedItemsSelected.map(i => i.id), ...lockedItemsSelected.map(i => i.id)]);
        if (idsToRemove.size === 0) return;

        // setSelectedItems(prev => prev.filter(i => !idsToRemove.has(i.id)));
        setUnlockedItems(prev => prev.filter(i => !idsToRemove.has(i.id)));
        setlockedItems(prev => prev.filter(i => !idsToRemove.has(i.id)));

        setUnlockedItemsSelected?.([]);
        setlockedItemsSelected?.([]);
    };

    const onLockedSelected = (item: VaultMediaResponseList) => {
        setlockedItemsSelected((prev: VaultMediaResponseList[]) => {
            const exists = prev.some(p => p.id === item.id);
            return exists ? prev.filter(p => p.id !== item.id) : [...prev, item];
        });
    };

    const onUnlockedSelected = (item: VaultMediaResponseList) => {
        setUnlockedItemsSelected((prev: VaultMediaResponseList[]) => {
            const exists = prev.some(p => p.id === item.id);
            return exists ? prev.filter(p => p.id !== item.id) : [...prev, item];
        });
    };

    const mediaSelected = (item: VaultMediaResponseList) => {
        if (unlockedItems.length + lockedItems.length >= 40) {
            return;
        }

        const { id } = item;
        const inUnlocked = unlockedItems.some(x => x.id === id);
        const inLocked = lockedItems.some(x => x.id === id);

        if (inUnlocked) {
            // remove from unlocked (and harden against a stray dup in locked)
            setUnlockedItems(prev => prev.filter(x => x.id !== id));
            setlockedItems(prev => prev.filter(x => x.id !== id));
        } else if (inLocked) {
            // remove from locked
            setlockedItems(prev => prev.filter(x => x.id !== id));
        } else {
            // add to locked
            setlockedItems(prev => (prev.some(x => x.id === id) ? prev : [...prev, item]));
            // ensure it's not present in unlocked (paranoia)
            setUnlockedItems(prev => prev.filter(x => x.id !== id));
            // auto-apply any creators / release forms attached to the newly selected media
            void applyMediaReleaseForms(id);
        }
    };

    useEffect(() => {
        setSelectedItems([...unlockedItems, ...lockedItems]);
    }, [unlockedItems, lockedItems]);

    const selectAllItems = (items: VaultMediaResponseList[]) => {
        // ids already present across BOTH buckets
        const existingIds = new Set<number>([...unlockedItems.map(i => i.id), ...lockedItems.map(i => i.id)]);

        const remaining = Math.max(0, 40 - existingIds.size);

        if (remaining === 0) return; // already full

        // choose up to `remaining` new items (no dups), keep incoming order
        const additions: VaultMediaResponseList[] = [];

        for (let i = 0; i < items.length; i += 1) {
            if (!existingIds.has(items[i].id)) {
                additions.push(items[i]);
                existingIds.add(items[i].id);

                if (additions.length >= remaining) break;
            }
        }
        if (additions.length === 0) return;

        // append to locked
        setlockedItems(prev => [...prev, ...additions]);
        // auto-apply any creators / release forms attached to the newly selected media
        additions.forEach(item => void applyMediaReleaseForms(item.id));
    };

    const deSelectAllItems = () => {
        setUnlockedItems([]);
        setlockedItems([]);
    };

    const lockedSelectedSet = useMemo(() => new Set(lockedItemsSelected.map(s => s.id)), [lockedItemsSelected]);
    const unlockedSelectedSet = useMemo(() => new Set(unlockedItemsSelected.map(s => s.id)), [unlockedItemsSelected]);

    const canMoveLeft = lockedItemsSelected.length > 0; // to Unlocked
    const canMoveRight = unlockedItemsSelected.length > 0; // to Locked
    const canTrash = canMoveLeft || canMoveRight;

    const onTaggedCreatorDelete = (id: number) => {
        const newCheckedLists = taggedCreators.filter(item => item.id !== id);

        onChangeTaggedCreators(newCheckedLists);
    };

    const onTaggedReleaseFormDelete = (id: number) => {
        setTaggedReleaseForms(prev => prev.filter(item => item.id !== id));
    };

    const setTaggedCreators = React.useCallback(
        (update: React.SetStateAction<{ id: number; name: string }[]>) => {
            const next =
                typeof update === 'function'
                    ? (update as (prev: { id: number; name: string }[]) => { id: number; name: string }[])(taggedCreators)
                    : update;

            onChangeTaggedCreators(next);
        },
        [taggedCreators, onChangeTaggedCreators],
    );

    // When a piece of media is selected, pull its attached release forms and auto-apply them so the
    // user doesn't have to tag creators / release (consent) forms manually. Existing tags are kept;
    // only new ones (by id) are added.
    const mergeById = (prev: { id: number; name: string }[], additions: { id: number; name: string }[]) => {
        const seen = new Set(prev.map(item => item.id));
        const toAdd = additions.filter(item => !seen.has(item.id));
        return toAdd.length === 0 ? prev : [...prev, ...toAdd];
    };

    const applyMediaReleaseForms = React.useCallback(
        async (mediaId: number) => {
            const { taggedCreators: creators, taggedReleaseForms: releaseForms } = await getVaultMediaTaggedItems(mediaId);

            if (creators.length > 0) {
                setTaggedCreators(prev => mergeById(prev, creators));
            }

            if (releaseForms.length > 0) {
                setTaggedReleaseForms(prev => mergeById(prev, releaseForms));
            }
        },
        [getVaultMediaTaggedItems, setTaggedCreators, setTaggedReleaseForms],
    );

    return (
        <Box>
            <Grid
                container
                wrap="nowrap"
                alignItems="stretch"
                sx={{
                    position: 'relative',
                    width: '100%',
                    minHeight: 170,
                    borderRadius: theme.shape.borderRadius,
                    overflow: 'hidden',
                    // marginLeft: 2,
                    // marginTop: 4,
                    // marginBottom: 2,
                    border: t => `1px solid ${alpha(t.palette.divider, 0.2)}`,
                }}
            >
                {(canMoveLeft || canTrash || canMoveRight) && (
                    <Paper
                        elevation={6}
                        sx={{
                            position: 'absolute',
                            left: '50%',
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            p: 0.5,
                            borderRadius: theme.shape.borderRadius,
                            backdropFilter: 'blur(6px)',
                            bgcolor: t => alpha(t.palette.background.paper, 0.9),
                            border: t => `1px solid ${alpha(t.palette.divider, 0.6)}`,
                            // keep it small on mobile
                            '@media (max-width:600px)': { p: 0.25 },
                        }}
                    >
                        {canMoveLeft && (
                            <Tooltip title="Move to Unlocked">
                                <span>
                                    <IconButton size="small" color="primary" onClick={onMoveSelectedToUnlocked} sx={{ p: 0.5 }}>
                                        <ChevronLeftRounded fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}

                        {canTrash && (
                            <Tooltip title="Remove selected">
                                <span>
                                    <IconButton size="small" color="error" onClick={onDeleteSelected} sx={{ p: 0.5 }}>
                                        <DeleteRounded fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}

                        {canMoveRight && (
                            <Tooltip title="Move to Locked">
                                <span>
                                    <IconButton size="small" color="primary" onClick={onMoveSelectedToLocked} sx={{ p: 0.5 }}>
                                        <ChevronRightRounded fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                    </Paper>
                )}

                {/* Left / Unlocked */}
                <Grid item xs zeroMinWidth sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    {/* Header */}
                    <Box
                        sx={{
                            px: 1,
                            py: 0.75,
                            borderBottom: t => `1px solid ${alpha(t.palette.divider, 0.3)}`,
                            height: '42px',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Unlocked
                        </Typography>
                        <Chip
                            label={`${unlockedItems.length}`}
                            size="small"
                            sx={{ ml: 1, fontFamily: theme.typography.caption.fontFamily, fontSize: theme.typography.caption.fontSize }}
                        />
                    </Box>
                    {/* Scroll row */}
                    {isLoading ? (
                        <LinearProgress />
                    ) : (
                        <HScroll>
                            {unlockedItems.length === 0 ? (
                                <Typography variant="caption" sx={{ px: 1.5, py: 1, color: 'text.disabled' }}>
                                    No unlocked media
                                </Typography>
                            ) : (
                                unlockedItems.map(m => (
                                    <VaultTile
                                        key={m.id}
                                        subscriber={subscriber}
                                        item={m}
                                        isSelected={unlockedSelectedSet.has(m.id)}
                                        handleMediaClicked={() => onUnlockedSelected(m)}
                                        allowDownload
                                    />
                                ))
                            )}
                        </HScroll>
                    )}
                </Grid>

                {/* Vertical Divider */}
                <Grid item>
                    <Divider orientation="vertical" flexItem />
                </Grid>

                {/* Right / Locked */}
                <Grid item xs zeroMinWidth sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    {/* Header (no blue background here) */}
                    <Box
                        sx={{
                            px: 1,
                            py: 0.75,
                            borderBottom: `1px solid ${rightHeaderBorder}`,
                            bgcolor: rightHeaderBg,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexWrap: 'nowrap',
                            height: '42px',
                        }}
                    >
                        <Stack direction="row" spacing={1} alignItems={'center'}>
                            <Typography variant="caption" sx={{ color: 'primary.main' }}>
                                Locked
                            </Typography>
                            <Chip
                                label={`${lockedItems.length}`}
                                size="small"
                                color="primary"
                                sx={{ ml: 1, fontFamily: theme.typography.caption.fontFamily, fontSize: theme.typography.caption.fontSize }}
                            />
                        </Stack>
                        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                            {/* Price input */}
                            <CompactPriceField value={lockedPrice} onChange={onPriceChange} />
                        </Box>
                    </Box>
                    {/* Scroll row with light blue coverage */}
                    {isLoading ? (
                        <LinearProgress />
                    ) : (
                        <HScroll sx={{ bgcolor: rightBg }}>
                            {lockedItems.length === 0 ? (
                                <Typography variant="caption" sx={{ px: 1.5, py: 1, color: 'text.disabled' }}>
                                    No locked media
                                </Typography>
                            ) : (
                                lockedItems.map(m => (
                                    <VaultTile
                                        key={m.id}
                                        subscriber={subscriber}
                                        item={m}
                                        isSelected={lockedSelectedSet.has(m.id)}
                                        handleMediaClicked={() => onLockedSelected(m)}
                                        allowDownload
                                    />
                                ))
                            )}
                        </HScroll>
                    )}
                </Grid>
            </Grid>

            <Grid container alignItems="center" wrap="nowrap" columnGap={1} marginTop={1}>
                {/* Chips take the rest of the row */}
                <Grid item xs zeroMinWidth>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, minWidth: 0 }}>
                        <Button onClick={() => setOpenTagCreators(true)}>Tag Creators</Button>
                    </Box>
                    {/* <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, minWidth: 0 }}>
                        {taggedCreators.length === 0 && <Typography variant="caption">No Creators Tags selected.</Typography>}
                    </Box> */}
                </Grid>

                {/* VaultMenu keeps natural width (e.g., a button) */}
                <Grid item>
                    <Button variant="contained" onClick={() => setOpen(true)}>
                        Open Vault
                    </Button>
                    <DialogAdaptive
                        open={open}
                        onClose={() => setOpen(false)}
                        fullScreen={fullScreen}
                        fullWidth={!fullScreen}
                        maxWidth="lg"
                        PaperProps={{
                            sx: {
                                // Fixed-height, non-scrolling flex column so the vault grid
                                // inside VaultMenu is the only scroll region
                                height: fullScreen ? '100%' : 'calc(100% - 64px)',
                                overflow: 'hidden',
                            },
                        }}
                    >
                        <StyledCardTitleBar
                            title="Select Media from Vault"
                            badge={
                                <IconButton color="warning" onClick={() => setOpen(false)}>
                                    <CloseIcon />
                                </IconButton>
                            }
                        />
                        <VaultMenu
                            // setOpen={setOpen}
                            subscriber={subscriber}
                            editOnly={false}
                            selectedItems={selectedItems}
                            onItemClick={mediaSelected}
                            selectAllItems={selectAllItems}
                            clearAllItems={deSelectAllItems}
                            fillHeight
                        />
                    </DialogAdaptive>
                </Grid>
            </Grid>

            <Grid container alignItems="center" wrap="nowrap" columnGap={1} marginTop={1}>
                {/* Chips take the rest of the row */}
                <Grid item xs zeroMinWidth>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, minWidth: 0 }}>
                        <SelectedChips items={taggedCreators} onDelete={onTaggedCreatorDelete} />
                    </Box>
                </Grid>
            </Grid>

            <Grid container alignItems="center" wrap="nowrap" columnGap={1} marginTop={1}>
                {/* Tagged release forms — displayed the same way as tagged creators */}
                <Grid item xs zeroMinWidth>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, minWidth: 0 }}>
                        <SelectedChips items={taggedReleaseForms} onDelete={onTaggedReleaseFormDelete} />
                    </Box>
                </Grid>
            </Grid>

            <DialogAdaptive open={openTagCreators} onClose={() => setOpenTagCreators(false)}>
                <StyledCardTitleBar title="Tag Creators" />

                <Box sx={{ p: 2 }}>
                    <OnlyFansTagCreatorsSelector
                        subscriber={subscriber}
                        tagCreators={tagCreators}
                        setTagCreators={setTagCreators}
                        taggedCreators={taggedCreators}
                        setTaggedCreators={setTaggedCreators}
                        taggedReleaseForms={taggedReleaseForms}
                        setTaggedReleaseForms={setTaggedReleaseForms}
                        session={null}
                        disabled={false}
                    />
                </Box>

                <StyledDialogActions>
                    <Button
                        onClick={() => {
                            setOpenTagCreators(false);
                        }}
                        color="inherit"
                    >
                        Close
                    </Button>
                </StyledDialogActions>
            </DialogAdaptive>
        </Box>
    );
}
